import { BalanceTypeTitles } from '@/entities/balances'
import { parseBalanceComment } from '@/entities/balances/lib/parseBalanceComment'
import { Currencies } from '@/entities/curency'
import TransactionIcon from '@/shared/assets/icons/transactionArrow.svg?react'
import { BaseCard, FinanceIcon, ItemLayout, StatusLabel } from '@/shared/components'
import { BaseCopyButton } from '@/shared/components/BaseCopyButton/BaseCopyButton'
import { formatCurrency } from '@/shared/lib/currency'
import { formatDate } from '@/shared/lib/date'
import { truncateHash } from '@/shared/lib/hash'
import { getShortenId } from '@/shared/utils/getShortenId'
import {
  BalanceAction,
  BalanceActionTitles,
  CryptoWithdrawStatusIcons,
  CryptoWithdrawStatusTitles,
  FinanceModel,
  TraderFinanceModel,
} from '../../model'

type Props = {
  finance: FinanceModel | TraderFinanceModel
  allData: FinanceModel[] | TraderFinanceModel[]
}

export const MobileBlockFinance = ({ finance, allData }: Props) => {
  const labelClasses = 'text-text-secondary'
  const rowClasses = 'flex justify-between items-center'

  const hasCryptoWithdraw = allData.some((item) => item.action === BalanceAction.CryptoWithdraw)

  if (hasCryptoWithdraw && finance.action === BalanceAction.CryptoWithdraw) {
    // Отображение для crypto withdraw операций
    const withdraw = finance.cryptoWithdraw
    const statusTitle = CryptoWithdrawStatusTitles[withdraw.status]
    const iconStatus = CryptoWithdrawStatusIcons[withdraw.status]

    const infoBlock = [
      {
        title: 'ID сделки:',
        component: (
          <div className="flex items-center gap-1">
            <span className="font-medium text-primary">{getShortenId(withdraw.id, 'long')}</span>
            <BaseCopyButton text={withdraw.id} color="primary" size="mini" />
          </div>
        ),
      },
      {
        title: 'Сумма:',
        component: (
          <span className="font-medium text-[18px]">
            {formatCurrency(Number(withdraw.amount), withdraw.currency)}
          </span>
        ),
      },
      {
        title: 'Статус:',
        component: <StatusLabel status={iconStatus}>{statusTitle}</StatusLabel>,
      },
      ...(withdraw.transactionHash
        ? [
            {
              title: 'Адрес транзакции:',
              component: (
                <a
                  href={`https://tronscan.org/#/transaction/${withdraw.transactionHash}`}
                  className="flex items-center gap-2"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="font-medium text-primary dark:text-primary-dark">
                    {truncateHash(withdraw.transactionHash)}
                  </span>
                  <TransactionIcon />
                </a>
              ),
            },
          ]
        : []),
    ]

    return (
      <BaseCard className="mb-4 px-5 text-sm">
        <div className="flex justify-between mb-6">
          <ItemLayout
            subtitle={formatDate(withdraw.createdAt)}
            title="Вывод прибыли"
            icon={<FinanceIcon status={iconStatus} transaction="withdrawal" type="arrow" />}
          />
        </div>
        <div className="flex flex-col gap-4">
          {infoBlock.map((el, index) => (
            <div key={index} className={rowClasses}>
              <span className={labelClasses}>{el.title}</span>
              {el.component}
            </div>
          ))}
        </div>
      </BaseCard>
    )
  }

  // Отображение для обычных операций
  const actionTitle = BalanceActionTitles[finance.action]
  const balanceTypeTitle = BalanceTypeTitles[finance.balanceType]
  const action = finance.action
  const amount = finance.amount
  const type = finance.action === BalanceAction.CryptoDeposit ? 'arrow' : 'sign'

  let transactionType: 'withdrawal' | 'deposit' = 'deposit'
  let statusColor: 'green' | 'red' | 'purple' = 'green'

  if (action === BalanceAction.CryptoWithdraw) {
    transactionType = 'withdrawal'
    statusColor = 'green'
  } else if (action === BalanceAction.AdminChange && amount < 0) {
    transactionType = 'withdrawal'
    statusColor = 'purple'
  } else if (action === BalanceAction.AdminChange && amount > 0) {
    transactionType = 'deposit'
    statusColor = 'purple'
  } else if (
    (action === BalanceAction.BalanceTransfer && amount > 0) ||
    action === BalanceAction.Deposit
  ) {
    transactionType = 'deposit'
    statusColor = 'green'
  } else if (action === BalanceAction.BalanceTransfer && amount < 0) {
    transactionType = 'withdrawal'
    statusColor = 'green'
  }

  const infoBlock = [
    {
      title: 'ID сделки:',
      component: (
        <div className="flex items-center gap-1">
          <span className="font-medium text-primary">{getShortenId(finance.id, 'long')}</span>
          <BaseCopyButton text={finance.id} color="primary" size="mini" />
        </div>
      ),
    },
    {
      title: 'Тип баланса:',
      component: <span className="text-sm">{balanceTypeTitle}</span>,
    },
    {
      title: 'Сумма:',
      component: (
        <span className="font-medium text-[18px]">
          {formatCurrency(Number(finance.amount), Currencies.USDT)}
        </span>
      ),
    },
    ...(finance.comment
      ? [
          {
            title: 'Комментарий:',
            component: (
              <div className="max-w-xs text-sm text-right break-words whitespace-pre-wrap">
                {parseBalanceComment(finance.comment)}
              </div>
            ),
          },
        ]
      : []),
  ]

  return (
    <BaseCard className="mb-4 px-5 text-sm">
      <div className="flex justify-between mb-6">
        <ItemLayout
          subtitle={formatDate(finance.createdAt)}
          title={actionTitle}
          icon={<FinanceIcon transaction={transactionType} status={statusColor} type={type} />}
        />
      </div>
      <div className="flex flex-col gap-4">
        {infoBlock.map((el, index) => (
          <div key={index} className={rowClasses}>
            <span className={labelClasses}>{el.title}</span>
            {el.component}
          </div>
        ))}
      </div>
    </BaseCard>
  )
}
